// frontend/packages/business-console/src/pages/Settings.jsx
//
// Settings hub (design handoff Page 10). One home for the Account-group
// pages, split into Account (you) and Workspace (the business), so the
// sidebar stays uncluttered: a single "Settings" nav item instead of five
// flat rows. A left sub-nav switches sections; the content area embeds the
// existing page components, which keep owning their own data + auth.
//
// The underlying routes (/profile, /billing-settings, /ai-usage,
// /stripe-connect) are intentionally kept so existing deep links and the
// Payments page's "Payout settings" link still work; this page is an
// additive hub, not a replacement. The active section is in `?section=` so
// it is deep-linkable and back-button friendly.
//
// Sections only cover what the platform actually has. The mock's 2FA /
// Team / Integrations / Notifications panels are intentionally absent
// (IdP-managed or not built), not faked.

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

import { useTokens, PageHeader, Surface } from '../components/primitives';
import Profile from './Profile';
import StripeConnectSettings from './StripeConnectSettings';
import BillingSettings from './BillingSettings';
import AiUsage from './AiUsage';
import BrandingSettings from './BrandingSettings';
import AnalyticsAlertsSettings from './AnalyticsAlertsSettings';
import ReportEmailsSettings from './ReportEmailsSettings';
import { getMyFeatures } from '../services/featuresService';
import { DISTRICT_MODE_FEATURE } from '../components/layout/districtMode';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import InboxReplySettings from './InboxReplySettings';
import PlanFeatures from './PlanFeatures';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

// Section registry. `group` drives the sub-nav captions (Account vs
// Workspace); `el` is rendered in the content area when selected.
const SECTIONS = [
  {
    key: 'profile',
    group: 'Account',
    label: 'Profile',
    Icon: PersonOutlineOutlinedIcon,
    el: <Profile />,
  },
  {
    key: 'branding',
    group: 'Workspace',
    label: 'Branding',
    Icon: PaletteOutlinedIcon,
    el: <BrandingSettings />,
  },
  {
    key: 'payments',
    group: 'Workspace',
    label: 'Payment settings',
    Icon: PaymentOutlinedIcon,
    el: <StripeConnectSettings />,
    // School district mode has no payments (components/layout/districtMode.js).
    hideInDistrictMode: true,
  },
  {
    key: 'billing',
    group: 'Workspace',
    label: 'Billing',
    Icon: NotificationsActiveOutlinedIcon,
    el: <BillingSettings />,
  },
  {
    key: 'ai-usage',
    group: 'Workspace',
    label: 'AI usage',
    Icon: AutoAwesomeOutlinedIcon,
    el: <AiUsage />,
  },
  {
    key: 'analytics-alerts',
    group: 'Workspace',
    label: 'Analytics alerts',
    Icon: InsightsOutlinedIcon,
    el: <AnalyticsAlertsSettings />,
  },
  {
    key: 'report-emails',
    group: 'Workspace',
    label: 'Report emails',
    Icon: ForwardToInboxOutlinedIcon,
    el: <ReportEmailsSettings />,
    // Only listed when this per-tenant feature is on (see visibleSections).
    feature: 'report_emails',
  },
  {
    key: 'reply-style',
    group: 'Workspace',
    label: 'Reply style',
    Icon: ChatBubbleOutlineOutlinedIcon,
    el: <InboxReplySettings />,
  },
  {
    key: 'features',
    group: 'Workspace',
    label: 'Plan & features',
    Icon: TuneOutlinedIcon,
    el: <PlanFeatures />,
  },
];

const GROUP_ORDER = ['Account', 'Workspace'];

export default function Settings() {
  const t = useTokens();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sections tied to a per-tenant feature stay hidden until it is on.
  const [enabledFeatures, setEnabledFeatures] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    getMyFeatures()
      .then((list) => {
        if (cancelled) return;
        setEnabledFeatures(
          new Set(list.filter((f) => f.enabled).map((f) => f.key))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const visibleSections = useMemo(
    () =>
      SECTIONS.filter(
        (s) =>
          (!s.feature || enabledFeatures.has(s.feature)) &&
          !(s.hideInDistrictMode && enabledFeatures.has(DISTRICT_MODE_FEATURE))
      ),
    [enabledFeatures]
  );

  const requested = searchParams.get('section');
  const active = visibleSections.some((s) => s.key === requested)
    ? requested
    : visibleSections[0].key;
  const activeSection = visibleSections.find((s) => s.key === active);

  const selectSection = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('section', key);
    setSearchParams(next, { replace: false });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1080, mx: 'auto' }}>
      <PageHeader title="Settings" sx={{ mb: 3 }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
          gap: { xs: 2, md: 2.5 },
          alignItems: 'start',
        }}
      >
        {/* Sub-nav */}
        <Surface sx={{ p: 1, position: { md: 'sticky' }, top: { md: 16 } }}>
          {GROUP_ORDER.map((group) => (
            <Box key={group} sx={{ mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  letterSpacing: '.7px',
                  textTransform: 'uppercase',
                  color: t.faint,
                  px: 1.25,
                  pt: 1.25,
                  pb: 0.5,
                }}
              >
                {group}
              </Typography>
              {visibleSections
                .filter((s) => s.group === group)
                .map((s) => {
                  const isActive = s.key === active;
                  const Icon = s.Icon;
                  return (
                    <Box
                      key={s.key}
                      component="button"
                      onClick={() => selectSection(s.key)}
                      sx={{
                        cursor: 'pointer',
                        font: 'inherit',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.25,
                        py: '9px',
                        mb: '2px',
                        borderRadius: '8px',
                        border: 'none',
                        textAlign: 'left',
                        bgcolor: isActive ? t.blueBg : 'transparent',
                        color: isActive ? t.blue : t.textSecondary,
                        fontSize: 13,
                        fontWeight: isActive ? 700 : 500,
                        '&:hover': { bgcolor: isActive ? t.blueBg : t.subtle },
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                      {s.label}
                    </Box>
                  );
                })}
            </Box>
          ))}
        </Surface>

        {/* Content: the selected section's existing page, embedded. */}
        <Box sx={{ minWidth: 0 }}>{activeSection?.el}</Box>
      </Box>
    </Box>
  );
}
